import { ServiceType } from '@aibees/elephant/components';
import { Maybe, Nullable } from '@aibees/shared/definition';
import { useDebounce, useQueryStringState } from '@aibees/shared/hooks';
import { useAutocomplete } from '@mui/material';
import { useEffect, useState } from 'react';
import useSelectionLimit from '../hooks/use-selection-limit';
import { AutocompleteType, SuggestionSearchBarOptionBase } from '../types';
import { Provider } from './context';
import { SuggestionSearchBarContextProps as ContextProps } from './types';

export const SuggestionSearchBarProvider = ({
  children,
  baseState,
}: ContextProps) => {
  const {
    detailActionElement,
    renderMode,
    searchIcon,
    searchCategory,
    multiple,
    fetchOptions,
    regex,
    defaultValue,
    limit,
    errorMessage = 'Invalid input',
    placeholderText,
    onDelete,
    onClickSearchButton,
    limitErrorMsg,
    deleteItemWithBackspace,
  } = baseState!;

  // --------------------------------------    states  --------------------------------
  const [searchKey, setSearchKey] = useState<Maybe<string>>();
  const [highlightedItem, setHighlightedItem] =
    useState<SuggestionSearchBarOptionBase>();
  const [inputError, setInputError] = useState<Nullable<string>>(null);
  const [values, setValues] = useState<SuggestionSearchBarOptionBase[]>(
    defaultValue ?? []
  );
  const { params } = useQueryStringState();
  // --------------------------------------    states  -------------------------------- //

  // --------------------------------------    hooks  -------------------------------- //
  const searchKeyDebounced = useDebounce(searchKey, 500);
  const limitReached = useSelectionLimit(values, limit);
  const {
    data,
    isFetching,
    isLoading: isOptionsLoading,
    error,
    isFetched,
  } = fetchOptions(searchKeyDebounced);

  const options = data?.content || [];
  // --------------------------------------    hooks  -------------------------------- //

  // --------------------------------------    effects  -------------------------------- //
  useEffect(() => {
    setHighlightedItem(undefined); // TODO remove this
    if (options.length > 0) {
      setHighlightedItem(options.at(0));
    }
  }, [options]);

  useEffect(() => {
    setValues(defaultValue ?? []);
  }, [defaultValue]);

  useEffect(() => {
    if (params.type === ServiceType.Analysis)
      handleInputChange(params.value ?? '');
  }, [params.type, params.value]);
  // --------------------------------------    effects  -------------------------------- //

  // --------------------------------------    handlers  --------------------------------
  const handleInputChange = (value: string) => {
    setSearchKey(value.length ? value : undefined);
    if (regex && !regex.test(value)) setInputError(errorMessage);
    else setInputError(null);
  };

  const onInputChange: AutocompleteType<SuggestionSearchBarOptionBase>['onInputChange'] =
    (e, value, reason) => {
      handleInputChange(value);
    };

  const handleHighlightItem = (item: SuggestionSearchBarOptionBase) => {
    setHighlightedItem(item);
  };

  const onChange: AutocompleteType<SuggestionSearchBarOptionBase>['onChange'] =
    (event, items, reason, detail) => {
      if (
        event.type !== 'keydown' &&
        (!limitReached || reason === 'removeOption')
      ) {
        setValues(items as SuggestionSearchBarOptionBase[]);
        setSearchKey(undefined);
      } else {
        setSearchKey(searchKey);
      }
    };

  const handleRemoveLookalike = (item: SuggestionSearchBarOptionBase) => {
    setValues((prev) => prev.filter((i) => i.id !== item.id));
  };

  const handleClearAll = () => {
    setValues([]);
  };
  // --------------------------------------    handlers  --------------------------------

  const {
    getRootProps,
    getInputProps,
    getTagProps,
    getListboxProps,
    getOptionProps,
    value,
    focused,
    setAnchorEl,
    popupOpen,
  } = useAutocomplete<
    SuggestionSearchBarOptionBase,
    typeof multiple,
    true,
    true
  >({
    freeSolo: true,
    id: `customized-hook-demo`,
    multiple,
    options,
    onInputChange,
    onChange,
    autoComplete: true,
    openOnFocus: true,
    disableCloseOnSelect: true,
    value: values,
    filterSelectedOptions: true,
  });
  const handleAddLookalike = (item: SuggestionSearchBarOptionBase) => {
    setValues((prev) => [...prev, item]);
    setSearchKey(undefined);
    getInputProps().onBlur?.({} as any);
  };

  const selectedItems = value as SuggestionSearchBarOptionBase[];

  return (
    <Provider
      value={{
        onDelete,
        renderMode,
        searchIcon,
        setAnchorEl,
        getTagProps,
        getRootProps,
        getInputProps,
        getOptionProps,
        searchCategory,
        getListboxProps,
        onClickSearchButton,
        detailActionElement,
        deleteItemWithBackspace,
        onClearAll: handleClearAll,
        onHighlightItem: handleHighlightItem,
        onAddLookalike: handleAddLookalike,
        onRemoveLookalike: handleRemoveLookalike,
        limit,
        options,
        focused,
        multiple,
        popupOpen,
        searchKey,
        defaultValue,
        limitReached,
        highlightedItem,
        selectedItems,
        limitErrorMsg,
        placeholderText,
        isOptionsLoading,
      }}
    >
      {children}
    </Provider>
  );
};
